import { Box, Flex, FormControl, FormHelperText, FormLabel, Text } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Currency, FrameworkAgreementDto, FrameworkAgreementPatchDto } from "../../autogen/bff-api";
import { useRunsPublicProcurements } from "../../common/organization/useRunsPublicProcurements";
import { FrameworkRegulationFields, isComplete, type FrameworkRegulation } from "./FrameworkRegulationFields";
import { useEditor } from "../../common/editing/save-coordinator";
import { displaySum } from "../../common/formatting/displaySum";
import { SectionCard } from "./SectionCard";

interface Props {
  frameworkAgreementId: string;
  canEdit: boolean;
}

export const CallOffRulesCard = ({ frameworkAgreementId, canEdit }: Props) => {
  const { t } = useTranslation();
  const { state, updateField, fieldError } = useEditor<FrameworkAgreementDto, FrameworkAgreementPatchDto>(
    frameworkAgreementId,
  );
  const runsPublicProcurements = useRunsPublicProcurements();
  const [unsavedLetUnder, setUnsavedLetUnder] = useState<FrameworkRegulation | null>(null);
  const ceiling = state.ceilingValue;
  const registeredByHand = !state.sourcingEventId;
  const savedLetUnder = { regulation: state.regulation ?? null, foaPart: state.foaPart ?? null };
  const letUnder = unsavedLetUnder ?? savedLetUnder;
  const regulationHelp = t(
    "The regulation the framework agreement was let under. Its mini-competitions show it under «Regulation».",
  );

  const changeLetUnder = (next: FrameworkRegulation) => {
    if (next.regulation !== null && !isComplete(next)) {
      setUnsavedLetUnder(next);
      return;
    }
    setUnsavedLetUnder(null);
    updateField({ regulation: next.regulation, foaPart: next.foaPart });
  };

  const competitionCeilingHelp = !ceiling
    ? t("The framework competition announced no estimated value, so call-offs are not limited.")
    : state.ceilingIsAnnouncedMaximum
      ? t("The maximum value the framework competition announced. No call-off can take the total past it.")
      : t("The estimated value the framework competition announced. No call-off can take the total past it.");
  const handRegisteredCeilingHelp = !ceiling
    ? t("No ceiling value is registered for the framework agreement, so call-offs are not limited.")
    : t("The ceiling value agreed in the framework agreement. No call-off can take the total past it.");

  return (
    <SectionCard title={t("Call-off rules")}>
      <Flex px={{ base: "4", md: "5" }} py="4" gap="6" flexWrap="wrap">
        <FormControl maxW="340px">
          <FormLabel as="div">{t("Call-off mechanism")}</FormLabel>
          <Text py="2">{t(state.callOffMechanism)}</Text>
          <FormHelperText>
            {registeredByHand
              ? t("Agreed in the framework agreement")
              : t("Set by the framework competition, and cannot be changed after award")}
          </FormHelperText>
        </FormControl>
        <FormControl maxW="340px">
          <FormLabel as="div">{t("Ceiling value")}</FormLabel>
          <Text py="2">
            {ceiling
              ? displaySum({ value: Number(ceiling.value), currency: ceiling.currency ?? Currency.Nok })
              : t("Not specified")}
          </Text>
          <FormHelperText>{registeredByHand ? handRegisteredCeilingHelp : competitionCeilingHelp}</FormHelperText>
        </FormControl>
        {registeredByHand && runsPublicProcurements && (
          <Box w="100%" maxW="340px" data-testid="framework-regulation">
            {canEdit ? (
              <FrameworkRegulationFields
                idPrefix="framework-regulation"
                value={letUnder}
                onChange={changeLetUnder}
                helperText={regulationHelp}
                errors={{ regulation: fieldError("regulation"), foaPart: fieldError("foaPart") }}
              />
            ) : (
              <FormControl>
                <FormLabel as="div">{t("Regulation")}</FormLabel>
                <Text py="2">
                  {letUnder.regulation
                    ? [t(letUnder.regulation), letUnder.foaPart ? t(letUnder.foaPart) : null].filter(Boolean).join(", ")
                    : t("Not stated")}
                </Text>
                <FormHelperText>{regulationHelp}</FormHelperText>
              </FormControl>
            )}
          </Box>
        )}
      </Flex>
    </SectionCard>
  );
};
